import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Award, Target, Users, TrendingUp, Truck, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Award: Award,
  Target: Target,
  Users: Users,
  TrendingUp: TrendingUp,
  Truck: Truck,
  ShieldCheck: ShieldCheck
};

const defaultContent = {
  title: "About Indicraft",
  subtitle: "Your trusted partner for premium quality products",
  story_title: "Our Story",
  story_content: "<p>Founded in 2026, Indicraft was born from a simple idea: to make premium quality products accessible to everyone across India. We believe that great products shouldn't come with unreasonable price tags.</p><p>What started as a small venture has grown into a trusted e-commerce platform serving thousands of satisfied customers. We carefully curate every product in our collection, ensuring it meets our high standards of quality and value.</p>",
  mission_title: "Our Mission",
  mission_content: "To provide premium quality products at affordable prices, with exceptional customer service and fast delivery across India. We aim to make online shopping simple, secure, and satisfying.",
  vision_title: "Our Vision",
  vision_content: "To become India's most trusted e-commerce platform, known for quality products, transparent pricing, and customer-first approach. We envision a shopping experience that delights every customer.",
  features: [
    { icon: "Award", title: "Premium Quality", description: "Every product is carefully selected and quality-tested before listing." },
    { icon: "TrendingUp", title: "Best Prices", description: "Unbeatable prices with regular discounts and special offers." },
    { icon: "Users", title: "Customer First", description: "Your satisfaction is our priority. 24/7 customer support available." },
    { icon: "Truck", title: "Fast Delivery", description: "Quick and reliable delivery across India with order tracking." }
  ],
  cta_title: "Join Thousands of Happy Customers",
  cta_subtitle: "Experience the difference of shopping with Indicraft today",
  cta_button_text: "Start Shopping",
  meta_title: "About Us | Indicraft",
  meta_description: "Learn about Indicraft - your trusted partner for premium quality Indian handicrafts."
};

const AboutPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  useEffect(() => {
    document.title = content.meta_title || "About Us | Indicraft";
  }, [content]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cms/about`);
      if (response.data) {
        setContent({ ...defaultContent, ...response.data });
      }
    } catch (error) {
      console.log('Using default content for About page');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Award;
    return IconComponent;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#064E3B]"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      <main className="flex-grow">
        <div className="bg-gradient-to-b from-white to-[#F8FAFC] py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-[#0f172a] mb-4" data-testid="about-title">
                {content.title}
              </h1>
              <p className="text-lg text-zinc-600">
                {content.subtitle}
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-8 mb-8">
              <h2 className="text-2xl font-semibold text-[#0f172a] mb-4">{content.story_title}</h2>
              <div 
                className="prose prose-zinc max-w-none prose-p:text-zinc-600 prose-p:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: content.story_content }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-[#F0FDF4] rounded-xl p-6 border border-[#86EFAC]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-[#064E3B] flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0f172a]">{content.mission_title}</h3>
                </div>
                <p className="text-zinc-700 leading-relaxed">
                  {content.mission_content}
                </p>
              </div>

              <div className="bg-[#FEF3C7] rounded-xl p-6 border border-[#FDE047]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-[#F97316] flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0f172a]">{content.vision_title}</h3>
                </div>
                <p className="text-zinc-700 leading-relaxed">
                  {content.vision_content}
                </p>
              </div>
            </div>

            {content.features && content.features.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-8 mb-8">
                <h2 className="text-2xl font-semibold text-[#0f172a] mb-6">Why Choose Us?</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {content.features.map((feature, index) => {
                    const IconComponent = getIcon(feature.icon);
                    return (
                      <div key={index} className="flex gap-4">
                        <div className="w-10 h-10 rounded-lg bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-5 h-5 text-[#064E3B]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#0f172a] mb-1">{feature.title}</h3>
                          <p className="text-sm text-zinc-600">{feature.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="bg-[#F8FAFC] rounded-xl p-8 text-center">
              <h2 className="text-2xl font-semibold text-[#0f172a] mb-3">{content.cta_title}</h2>
              <p className="text-zinc-600 mb-6">
                {content.cta_subtitle}
              </p>
              <Button
                onClick={() => navigate('/shop')}
                className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold px-8 py-4 rounded-full transition-all shadow-lg hover:shadow-xl"
                data-testid="about-cta-button"
              >
                {content.cta_button_text}
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
